import { Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { caixaService } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { dateFormat } from '../../../shared/helpers/dateFormat';

const formatCurrency = (value: number) => `R$ ${value.toFixed(2)}`;

export const BusinessCaixa: React.FC<IPropsBusinessPage> = ({ business }) => {
  const queryClient = useQueryClient();
  const [openingBalance, setOpeningBalance] = useState(0);

  const { data: currentCaixa, isFetching: isFetchingCurrent } = useQuery({
    queryKey: ['caixaCurrent', business.id],
    queryFn: () => caixaService.getCurrent(business.id),
    staleTime: 0,
  });

  const { data: caixaHistory, isFetching: isFetchingHistory } = useQuery({
    queryKey: ['caixaHistory', business.id],
    queryFn: () => caixaService.getByBusiness(business.id),
    staleTime: 0,
  });

  const { mutate: openCaixa, isLoading: isOpening } = useMutation({
    mutationFn: () => caixaService.open({ businessId: business.id, openingBalance }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caixaCurrent'] });
      queryClient.invalidateQueries({ queryKey: ['caixaHistory'] });
      setOpeningBalance(0);
    },
  });

  const { mutate: closeCaixa, isLoading: isClosing } = useMutation({
    mutationFn: () => caixaService.close({ 
      id: currentCaixa!.id,
      businessId: currentCaixa!.businessId,
      closingBalance: currentCaixa!.openingBalance + currentCaixa!.totalGeral - currentCaixa!.totalExpense 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caixaCurrent'] });
      queryClient.invalidateQueries({ queryKey: ['caixaHistory'] });
    },
  });

  if (isFetchingCurrent) {
    return <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>;
  }

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Current Caixa Status */}
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom data-testid='caixa-status'>Caixa {currentCaixa?.isOpen ? 'Aberto' : 'Fechado'}</Typography>
          
          {currentCaixa?.isOpen ? (
            <>
              <Box display='flex' flexWrap='wrap' gap={1} mb={2}>
                <Typography variant='caption' color='text.secondary'>
                  Aberto em: {dateFormat(currentCaixa.openedAt, 'DD/MM/YYYY HH:mm')}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Aberto por: {currentCaixa.openedByName}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant='caption' color='text.secondary'>Saldo Inicial</Typography>
                  <Typography variant='h6'>{formatCurrency(currentCaixa.openingBalance)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant='caption' color='text.secondary'>Recebido</Typography>
                  <Typography variant='h6' color='success.main'>{formatCurrency(currentCaixa.totalGeral)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant='caption' color='text.secondary'>Despesas</Typography>
                  <Typography variant='h6' color='error.main'>{formatCurrency(currentCaixa.totalExpense)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant='caption' color='text.secondary'>Saldo Final</Typography>
                  <Typography variant='h6' color='primary.main'>
                    {formatCurrency(currentCaixa.openingBalance + currentCaixa.totalGeral - currentCaixa.totalExpense)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant='subtitle2' gutterBottom>Recebimentos por Forma</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4} sm={4} md={2}>
                  <Typography variant='caption' color='text.secondary'>Dinheiro</Typography>
                  <Typography variant='body1'>{formatCurrency(currentCaixa.totalDinheiro)}</Typography>
                </Grid>
                <Grid item xs={4} sm={4} md={2}>
                  <Typography variant='caption' color='text.secondary'>PIX</Typography>
                  <Typography variant='body1'>{formatCurrency(currentCaixa.totalPix)}</Typography>
                </Grid>
                <Grid item xs={4} sm={4} md={2}>
                  <Typography variant='caption' color='text.secondary'>Débito</Typography>
                  <Typography variant='body1'>{formatCurrency(currentCaixa.totalDebito)}</Typography>
                </Grid>
                <Grid item xs={4} sm={4} md={2}>
                  <Typography variant='caption' color='text.secondary'>Crédito</Typography>
                  <Typography variant='body1'>{formatCurrency(currentCaixa.totalCredito)}</Typography>
                </Grid>
                <Grid item xs={4} sm={4} md={2}>
                  <Typography variant='caption' color='text.secondary'>Outro</Typography>
                  <Typography variant='body1'>{formatCurrency(currentCaixa.totalOutro)}</Typography>
                </Grid>
              </Grid>

              <Box mt={3}>
                <Button
                  variant='contained'
                  color='error'
                  onClick={() => closeCaixa()}
                  disabled={isClosing}
                  fullWidth
                  data-testid='btn-close-caixa'
                >
                  Fechar Caixa
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant='body2' color='text.secondary' paragraph>
                Para abrir o caixa, informe o saldo inicial em dinheiro.
              </Typography>
              <TextField
                label='Saldo Inicial'
                value={openingBalance > 0 ? openingBalance.toFixed(2) : ''}
                placeholder='0,00'
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setOpeningBalance(raw ? parseFloat(raw) / 100 : 0);
                }}
                fullWidth
                size='small'
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <Typography variant='body2' sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>,
                }}
                inputProps={{
                  inputMode: 'decimal',
                }}
              />
              <Button
                variant='contained'
                onClick={() => openCaixa()}
                disabled={isOpening}
                fullWidth
                data-testid='btn-open-caixa'
              >
                Abrir Caixa
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* History */}
      {!isFetchingHistory && caixaHistory && caixaHistory.length > 0 && (
        <>
          <Typography variant='h6'>Histórico</Typography>
          {caixaHistory.map((caixa) => (
            <Card key={caixa.id}>
              <CardContent>
                <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                  <Typography variant='subtitle1'>
                    {dateFormat(caixa.openedAt, 'DD/MM/YYYY')}
                  </Typography>
                  <Chip 
                    label={caixa.isOpen ? 'Aberto' : 'Fechado'} 
                    color={caixa.isOpen ? 'success' : 'default'}
                    size='small'
                  />
                </Box>
                <Typography variant='body2' color='text.secondary'>
                  {dateFormat(caixa.openedAt, 'HH:mm')} - {caixa.closedAt ? dateFormat(caixa.closedAt, 'HH:mm') : '---'}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Aberto por: {caixa.openedByName}
                  {caixa.closedByName ? ` · Fechado por: ${caixa.closedByName}` : ' · Fechado automaticamente'}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant='caption' color='text.secondary'>Entrada</Typography>
                    <Typography variant='body2' color='success.main'>+{formatCurrency(caixa.totalGeral)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant='caption' color='text.secondary'>Saída</Typography>
                    <Typography variant='body2' color='error.main'>-{formatCurrency(caixa.totalExpense)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant='caption' color='text.secondary'>Saldo Final</Typography>
                    <Typography variant='body2'>
                      {formatCurrency((caixa.openingBalance + caixa.totalGeral - caixa.totalExpense))}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </Box>
  );
};